// src/encounter/previous-notes-loader.tsx
// [PROD-1523] `?x=previous-notes`: reads a follow-up pre-visit's earlier notes and hands the rows to the shell. Mounted only
// when the switch is on (like <NoteMacroLibrary>), so the OFF page issues no query. Renders nothing.
// A failed read (other than 404) hands no rows, so the section stays hidden: PreviousNotesSection has no error state yet.
import { useEffect, useMemo } from 'react';
import { usePreviousEncounterNotes, toPreviousNotesInput } from './services/use-previous-encounter-notes';
import { toPreviousNotes, type PreviousNoteView } from './lib/to-previous-notes';

export interface PreviousNotesLoaderProps {
  /** Flutter providerId(): the signed-in user's id. */
  userId: string | null | undefined;
  patientId: string | null | undefined;
  /** The encounter's reason-for-visit code (asked with) and name (the row title). */
  reasonForVisitCode: string | null | undefined;
  reasonForVisitName: string | null | undefined;
  /** The current encounter, left out of the list. */
  currentEncounterId: string | null | undefined;
  isFollowUp: boolean;
  onNotes: (notes: PreviousNoteView[]) => void;
}

export function PreviousNotesLoader({ userId, patientId, reasonForVisitCode, reasonForVisitName, currentEncounterId, isFollowUp, onNotes }: PreviousNotesLoaderProps) {
  const query = usePreviousEncounterNotes({ userId, patientId, reasonForVisitCode, isFollowUp, enabled: true });
  const notes = useMemo(
    () =>
      isFollowUp
        ? toPreviousNotes({
            ...toPreviousNotesInput(query.data),
            reasonForVisit: reasonForVisitName ?? undefined,
            currentEncounterId: currentEncounterId ?? undefined,
          })
        : [],
    [isFollowUp, query.data, reasonForVisitName, currentEncounterId],
  );
  useEffect(() => onNotes(notes), [notes, onNotes]);
  return null;
}
